import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { KDialog, KSelectAutocomplete } from "@kyburz/kdesign-system";
import useSetState from "../../../../hooks/useSetState";
import { setAlert } from "../../../../redux/actions/actions";
import { getHardwareTypes } from "../../../../apiCalls/admin/hardwareTypes";
import { getCustomers } from "../../../../apiCalls/admin/customers";
import { getDeviceTemplates } from "../../../../apiCalls/admin/deviceTemplates";
import { importDevices } from "../../../../apiCalls/config/devices";

interface Props {
  deviceImeis: string[];
  open: boolean;
  togglePopup: (e: any, reload: boolean) => void;
}

// TODO: maybe fetch this from the backend
const deviceSIMTypes = [
  { name: "None", label: "None" },
  { name: "Local", label: "Local" },
  { name: "Global", label: "Global" },
];

const BulkUpdatePopup = ({ deviceImeis, open, togglePopup }: Props) => {
  const INITIAL_STATE = {
    hardwareTypes: [],
    customers: [],
    deviceTemplates: [],
    selectedHardwareType: null,
    selectedCustomer: null,
    selectedDeviceTemplate: null,
    selectedSIMType: null,
  };
  const dispatch = useDispatch();

  const [
    {
      hardwareTypes,
      customers,
      deviceTemplates,
      selectedHardwareType,
      selectedCustomer,
      selectedDeviceTemplate,
      selectedSIMType,
    },
    setState,
  ] = useSetState({
    ...INITIAL_STATE,
  });

  useEffect(() => {
    const hwTypes = getHardwareTypes(null).then((res) => {
      return res?.data?.map((t) => ({ name: t, label: t }));
    });
    const cst = getCustomers(null).then((res) => {
      return res?.data?.map((t) => ({ ...t, label: t.name }));
    });
    const tmpl = getDeviceTemplates(null).then((res) => {
      return res?.data?.data?.map((t) => ({ ...t, label: t.name }));
    });
    Promise.all([hwTypes, cst, tmpl]).then((result) =>
      setState({
        hardwareTypes: result[0],
        customers: result[1],
        deviceTemplates: result[2],
      }),
    );
  }, []);

  const handleSelectChange = (name, value) =>
    setState({
      [name]: value,
    });

  const closePopup = (reload: boolean = false) => {
    setState({
      selectedTemplate: null,
      selectedModel: null,
      selectedGroup: null,
    });
    togglePopup(null, reload);
  };

  const save = () => {
    const region = localStorage.getItem("region");
    let devices = deviceImeis.map((imei) => ({
      imei,
    }));
    if (
      selectedDeviceTemplate?.name !== null &&
      selectedDeviceTemplate?.name !== "" &&
      typeof selectedDeviceTemplate?.name !== "undefined"
    ) {
      devices = devices.map((d) => ({
        ...d,
        template: selectedDeviceTemplate.name,
      }));
    }
    if (
      selectedCustomer?.name !== null &&
      selectedCustomer?.name !== "" &&
      typeof selectedCustomer?.name !== "undefined"
    ) {
      devices = devices.map((d) => ({ ...d, customer: selectedCustomer.name }));
    }
    if (
      selectedHardwareType?.name !== null &&
      selectedHardwareType?.name !== "" &&
      typeof selectedHardwareType?.name !== "undefined"
    ) {
      devices = devices.map((d) => ({
        ...d,
        hardware_type: selectedHardwareType.name,
      }));
    }
    if (
      selectedSIMType?.name !== null &&
      selectedSIMType?.name !== "" &&
      typeof selectedSIMType?.name !== "undefined"
    ) {
      devices = devices.map((d) => ({ ...d, sim_type: selectedSIMType.name?.toLowerCase() }));
    }
    const importDevicesPromise = importDevices(devices, region);
    const promises = [importDevicesPromise];
    Promise.all(promises)
      .then((result) => {
        console.log(result); // leave this here, so the exact error can be debugged if required
        if (result.length > 0) {
          const res: any = result[0];
          const failed = res?.data?.failed || 0; // number of errors
          const errors = res?.data?.errors || []; // array of error messages
          const err = errors.join(", ");
          const updated = res?.data?.updated || 0; // number of updated devices
          dispatch(
            setAlert(
              `Result: ${
                res?.data?.result || "unknown"
              } - ${updated} devices updated, ${failed} devices failed ${
                failed > 0 ? `(error messages: ${err})` : ""
              }`,
              failed > 0 ? "error" : "success",
            ),
          );
        }
      })
      .finally(() => {
        closePopup(true);
      });
  };

  return (
    <KDialog
      size="xs"
      title="Update Devices"
      open={open}
      primaryCTAonClick={save}
      primaryCTAText="Update"
      secondaryCTAonClick={closePopup as any}
      secondaryCTAText="Cancel"
      hasOverflow
    >
      <KSelectAutocomplete
        placeholder="Select Hardware Type"
        name="selectedHardwareType"
        elements={hardwareTypes}
        onChange={handleSelectChange}
        value={selectedHardwareType?.label}
        label="Hardware Type"
        width="100%"
        style={{ width: "100%" }}
        isClearable
      />
      <KSelectAutocomplete
        placeholder="Customer short name"
        name="selectedCustomer"
        elements={customers}
        onChange={handleSelectChange}
        value={selectedCustomer?.label}
        label="Customer"
        width="100%"
        style={{ width: "100%" }}
        isClearable
      />
      <KSelectAutocomplete
        placeholder="Device Template Name"
        name="selectedDeviceTemplate"
        elements={deviceTemplates}
        onChange={handleSelectChange}
        value={selectedDeviceTemplate?.label}
        label="Template"
        width="100%"
        style={{ width: "100%" }}
        isClearable
      />
      <KSelectAutocomplete
        placeholder="Device SIM type"
        name="selectedSIMType"
        elements={deviceSIMTypes}
        onChange={handleSelectChange}
        value={selectedSIMType?.label}
        label="SIM Type"
        width="100%"
        style={{ width: "100%" }}
        isClearable
      />
    </KDialog>
  );
};

export default BulkUpdatePopup;
